import { useState, useCallback, useMemo } from "react";

export type ErrorState = {
  type: "error";
  errorMessage: string;
};

export type MarkdownEditorState =
  | {
      type: "default" | "success";
    }
  | ErrorState;

type MarkdownEditorControllerProps = {
  errorMessages?: string | string[] | undefined;
  onBlur?: () => void;
  onChange: (value: string) => void;
};

export type MarkdownEditorController = {
  formState: MarkdownEditorState;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onError?: (error?: ErrorState) => void;
};

const manageState = (touched: boolean): MarkdownEditorState => {
  if (touched) {
    return {
      type: "success",
    };
  }
  return {
    type: "default",
  };
};

export const useMarkdownEditorController = ({
  onChange,
  errorMessages,
  onBlur,
}: MarkdownEditorControllerProps): {
  controller: MarkdownEditorController;
} => {
  const [touched, setTouched] = useState(false);

  const handleOnChange = useCallback(
    (value: string) => {
      onChange(value);
      setTouched(true);
    },
    [onChange],
  );

  const formState = useMemo(() => {
    return errorMessages && errorMessages.length > 0
      ? ({
          type: "error",
          errorMessage: Array.isArray(errorMessages)
            ? errorMessages.join("\n")
            : errorMessages,
        } as ErrorState)
      : manageState(touched);
  }, [errorMessages, touched]);

  const handleOnBlur = useCallback(() => {
    onBlur && onBlur();
    setTouched(true);
  }, [onBlur]);

  const controller = useMemo(() => {
    return {
      formState,
      onChange: handleOnChange,
      onBlur: handleOnBlur,
    };
  }, [formState, handleOnBlur, handleOnChange]);

  return { controller };
};
